import {
  createBrowserRouter,
  createMemoryRouter,
  Navigate,
  type RouteObject,
} from 'react-router-dom'

import { LoginCallbackPage, LoginPage } from '@/features/auth'
import { DeviceShowPage, DevicesPage } from '@/features/devices'
import { JobShowPage, RaiseJobPage } from '@/features/jobs'
import {
  AbortedJobsReportPage,
  CompletedJobsReportPage,
  ReportsPage,
  ServiceLevelAgreementReportPage,
} from '@/features/reports'
import { CurrentUserRoute } from '@/routes/current-user-route'
import { HomeRoute, PortalRoute, RoleAwareLayout } from '@/routes/role-routes'
import { ProtectedRoute } from '@/routes/protected-route'
import { UnauthenticatedRoute } from '@/routes/unauthenticated-route'

export const appRoutes: RouteObject[] = [
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <CurrentUserRoute />,
        children: [
          {
            element: <RoleAwareLayout />,
            children: [
              {
                path: '/',
                element: <HomeRoute />,
                handle: {
                  title: 'Jobs',
                  breadcrumbs: [{ label: 'Jobs' }],
                },
              },
              {
                element: <PortalRoute />,
                children: [
                  {
                    path: '/devices',
                    element: <DevicesPage />,
                    handle: {
                      title: 'Devices',
                      breadcrumbs: [{ label: 'Devices' }],
                    },
                  },
                  {
                    path: '/devices/:id',
                    element: <DeviceShowPage />,
                    handle: {
                      title: 'Device detail',
                      breadcrumbs: [
                        { label: 'Devices', to: '/devices' },
                        { label: 'Device' },
                      ],
                    },
                  },
                  {
                    path: '/jobs/new',
                    element: <RaiseJobPage />,
                    handle: {
                      title: 'Raise new job',
                      breadcrumbs: [
                        { label: 'Jobs', to: '/' },
                        { label: 'Raise new job' },
                      ],
                    },
                  },
                  {
                    path: '/jobs/:id',
                    element: <JobShowPage />,
                    handle: {
                      title: 'Job detail',
                      breadcrumbs: [
                        { label: 'Jobs', to: '/' },
                        { label: 'Job' },
                      ],
                    },
                  },
                  {
                    path: '/reports',
                    element: <ReportsPage />,
                    handle: {
                      title: 'Reports',
                      breadcrumbs: [{ label: 'Reports' }],
                    },
                  },
                  {
                    element: <AbortedJobsReportPage />,
                    children: [
                      {
                        path: '/reports/aborted-jobs',
                        handle: {
                          title: 'Aborted jobs by reason',
                          breadcrumbs: [
                            { label: 'Reports', to: '/reports' },
                            { label: 'Aborted jobs by reason' },
                          ],
                        },
                      },
                      {
                        path: '/reports/aborted-jobs-by-partner',
                        handle: {
                          title: 'Aborted jobs by SiteWorks partner',
                          breadcrumbs: [
                            { label: 'Reports', to: '/reports' },
                            { label: 'Aborted jobs by SiteWorks partner' },
                          ],
                        },
                      },
                    ],
                  },
                  {
                    element: <CompletedJobsReportPage />,
                    children: [
                      {
                        path: '/reports/completed-jobs',
                        handle: {
                          title: 'Completed jobs by outcome',
                          breadcrumbs: [
                            { label: 'Reports', to: '/reports' },
                            { label: 'Completed jobs by outcome' },
                          ],
                        },
                      },
                      {
                        path: '/reports/completed-jobs-by-partner',
                        handle: {
                          title: 'Completed jobs by SiteWorks partner',
                          breadcrumbs: [
                            { label: 'Reports', to: '/reports' },
                            { label: 'Completed jobs by SiteWorks partner' },
                          ],
                        },
                      },
                    ],
                  },
                  {
                    element: <ServiceLevelAgreementReportPage />,
                    children: [
                      {
                        path: '/reports/service-level-agreement',
                        handle: {
                          title: 'Service Level Agreement',
                          breadcrumbs: [
                            { label: 'Reports', to: '/reports' },
                            {
                              label: 'Service Level Agreement',
                              to: '/reports/service-level-agreement',
                            },
                          ],
                        },
                      },
                      {
                        path: '/reports/service-level-agreement-by-partner',
                        handle: {
                          title: 'Service Level Agreement by SiteWorks partner',
                          breadcrumbs: [
                            { label: 'Reports', to: '/reports' },
                            {
                              label: 'Service Level Agreement',
                              to: '/reports/service-level-agreement',
                            },
                            {
                              label:
                                'Service Level Agreement by SiteWorks partner',
                            },
                          ],
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    element: <UnauthenticatedRoute />,
    children: [
      { path: '/login', element: <LoginPage /> },
    ],
  },
  {
    path: '/login/callback',
    element: <LoginCallbackPage />,
  },
  { path: '*', element: <Navigate to="/" replace /> },
]

export const appRouter = createBrowserRouter(appRoutes)

export const createAppMemoryRouter = (initialEntries: string[]) =>
  createMemoryRouter(appRoutes, { initialEntries })
